import { get, patch, post } from "@/services/axiosUitils";

const basePath = "/formily/dynamic-model";
const PROJECT_SCHEMA_NAME = "PTS Project";
const TASK_SCHEMA_NAME = "PTS Task";

class PtsProjectService {
  /**
   * Search projects by projectname
   * Calls the base endpoint: /formily/dynamic-model/PTS%20Project/?projectname__ilike=%dg%
   * @param params - Search parameters
   * @param params.projectname - Project name search term (will be wrapped with % for ilike)
   * @param params.project_type1 - Project type filter (e.g., 'external')
   * @param params.page - Page number (optional, not included if not provided)
   * @param params.pageSize - Page size (optional, not included if not provided)
   * @returns Project data
   */
  searchProjects = async ({
    projectname,
    project_type1,
    page,
    pageSize,
  }: {
    projectname?: string;
    project_type1?: string;
    page?: number;
    pageSize?: number;
  }): Promise<any> => {
    const params: any = {};

    // Add pagination only if provided
    if (page !== undefined) {
      params.page = page;
    }
    if (pageSize !== undefined) {
      params.page_size = pageSize;
    }

    // Add projectname filter if provided
    if (projectname?.trim()) {
      params.projectname__ilike = `%${projectname.trim()}%`;
    }

    // Add project_type1 filter if provided
    if (project_type1?.trim()) {
      params.project_type1 = project_type1.trim();
    }

    return await get(
      `${basePath}/${encodeURIComponent(PROJECT_SCHEMA_NAME)}/`,
      {
        params,
      }
    );
  };

  /**
   * Get projects with custom filters
   * Calls the base endpoint: /formily/dynamic-model/PTS%20Project/?filter=value
   * @param filters - Custom filter object (e.g., { projectname__ilike: '%dg%', project_type1: 'external' })
   * @param page - Page number (optional, not included if not provided)
   * @param pageSize - Page size (optional, not included if not provided)
   * @returns Project data
   */
  getProjects = async (
    filters?: Record<string, any>,
    page?: number,
    pageSize?: number
  ): Promise<any> => {
    const params: any = { ...filters };

    params.is_pts_project = true;
    params.ordering = "projectname";

    // Add pagination only if provided
    if (page !== undefined) {
      params.page = page;
    }
    if (pageSize !== undefined) {
      params.page_size = pageSize;
    }

    // Custom paramsSerializer to preserve % signs in filter values
    // This ensures %dg% appears as %dg% in URL, not %25dg%25
    const paramsSerializer = (params: any) => {
      const parts: string[] = [];
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value !== undefined && value !== null) {
          // Encode the key
          const encodedKey = encodeURIComponent(key);
          // For filter values that contain %, preserve % signs but encode other special chars
          if (typeof value === "string" && value.includes("%")) {
            // Encode everything except % signs
            const encodedValue = value
              .split("%")
              .map(part => encodeURIComponent(part))
              .join("%");
            parts.push(`${encodedKey}=${encodedValue}`);
          } else {
            // Normal encoding for other values
            parts.push(`${encodedKey}=${encodeURIComponent(String(value))}`);
          }
        }
      });
      return parts.join("&");
    };

    return await get(
      `${basePath}/${encodeURIComponent(PROJECT_SCHEMA_NAME)}/`,
      {
        params,
        paramsSerializer,
      }
    );
  };

  /**
   * Get project summary with status counts
   * @param projectId - Optional project ID to filter by specific project
   * @returns Project summary data with status counts
   */
  getProjectSummary = async (projectId?: string): Promise<any> => {
    const params: any = {};
    if (projectId) {
      params.project_id = projectId;
    }
    return await get("/pts_app/project-summary/", { params });
  };

  /**
   * Get project summary kanban data with task listings by status
   * @param projectId - Optional project ID to filter by specific project
   * @returns Project summary kanban data
   */
  getProjectSummaryKanban = async (projectId?: string): Promise<any> => {
    const params: any = {};
    if (projectId) {
      params.project_id = projectId;
    }
    return await get("/pts_app/project-summary/kanban-view/", { params });
  };

  /**
   * Update a PTS task status
   * Calls: /formily/dynamic-model/PTS%20Task/{taskId}/
   * @param taskId - Task ID
   * @param statusId - Status record ID
   * @returns Updated task response
   */
  updateTaskStatus = async (taskId: string, statusId: string): Promise<any> => {
    if (!taskId || !statusId) {
      throw new Error("Task ID and status ID are required");
    }

    return await patch(
      `${basePath}/${encodeURIComponent(TASK_SCHEMA_NAME)}/${taskId}/`,
      {
        status: statusId,
      }
    );
  };

  /**
   * Save project as template
   * Calls: /pts_app/save-project-as-template/?project_id={project_id}
   * @param projectId - Project ID to save as template
   * @param isModel - When true, passes is_model=true (used after user confirms)
   * @returns API response
   */
  saveProjectAsTemplate = async (
    projectId: string,
    isModel = false
  ): Promise<any> => {
    if (!projectId) {
      throw new Error("Project ID is required");
    }

    const params: Record<string, any> = { project_id: projectId };
    if (isModel) {
      // Backend expects is_model=true for confirmed repeated template use
      params.is_model = true;
    }

    return await get("/pts_app/save-project-as-template/", {
      params,
    });
  };

  /**
   * Clone project from template (use template)
   * Calls: /pts_app/save-project-as-template/clone-project/?project_id={project_id}
   * @param projectId - Template project ID to clone
   * @returns API response
   */
  cloneProjectFromTemplate = async (projectId: string): Promise<any> => {
    if (!projectId) {
      throw new Error("Project ID is required");
    }
    return await get("/pts_app/save-project-as-template/clone-project/", {
      params: { project_id: projectId },
    });
  };

  /**
   * Get project templates list
   * Calls: /pts_app/project-template/
   * @returns Project templates data: { data: Array<{ projectname, duration, description, project_type, billing_model, 'total-task' }> }
   */
  getProjectTemplates = async (): Promise<{
    data: Array<{
      id?: string;
      project_id?: string;
      projectname: string;
      duration: string | null;
      description: string;
      project_type: string;
      billing_model: string;
      "total-task": number;
    }>;
  }> => {
    return await get("/pts_app/project-template/");
  };

  /**
   * Get project template details by project_id (tasks, etc.)
   * POST /pts_app/project-template/project-details/
   * Body: { project_id: string }
   */
  getProjectTemplateDetails = async (
    projectId: string
  ): Promise<{
    data: {
      "project-name": string;
      duration: string | null;
      created_by: string;
      description: string;
      project_type: string;
      billing_model: string;
      task_listing: Array<{
        id: string;
        taskname: string;
        project: string;
        taskestimatedhours: string;
        actualhours: string;
        variance: string;
        parent_task: string | null;
        subtask?: Array<{
          id: string;
          taskname: string;
          taskestimatedhours: string;
          actualhours: string;
          variance: string;
          parent_task: string;
        }>;
      }>;
    };
  }> => {
    return await post("/pts_app/project-template/project-details/", {
      project_id: projectId,
    });
  };

  /**
   * Get project client billing details for invoice Bill To section
   * Calls: /pts_app/project-client-data/?project_id={projectId}
   */
  getProjectClientData = async (
    projectId: string
  ): Promise<{
    data?: Array<{
      id: string;
      is_active: boolean;
      client_project: string;
      client_name: string;
      address: string;
      mobile_number: string;
      email: string;
      gst_types: string;
      gst_no?: string;
      gst_applicable?: boolean;
      gst_rate?: string | number;
    }>;
  }> => {
    if (!projectId) {
      throw new Error("Project ID is required");
    }

    return await get("/pts_app/project-client-data/", {
      params: { project_id: projectId },
    });
  };

  /**
   * Get open project list for dropdown
   * Calls: /pts_app/project-summary/open-project-list/
   * @param options.emp_id - When set, limits projects to those open for that employee (e.g. logged-in user).
   * @returns Open project list data formatted as { value: string, label: string }[]
   */
  getOpenProjectList = async (options?: {
    emp_id?: string | null;
  }): Promise<Array<{ value: string; label: string }>> => {
    const url = `/pts_app/project-summary/open-project-list/`;
    const empId = options?.emp_id;
    const hasEmpId =
      empId !== undefined && empId !== null && String(empId).trim() !== "";
    const response = await get<{
      success: boolean;
      project: Array<{ id: string; projectname: string }>;
    }>(url, {
      ...(hasEmpId ? { params: { emp_id: String(empId) } } : {}),
    });

    // Transform API response to dropdown format
    if (response?.project && Array.isArray(response.project)) {
      return response.project.map(project => ({
        value: project.id,
        label: project.projectname,
      }));
    }

    return [];
  };
}

const instance = new PtsProjectService();
export default instance;
